import { useCallback, useEffect, useMemo, useState } from "react";

import {
  activateReviewTemplate,
  ApprovedFieldProjection,
  ApprovedFieldUsageProjection,
  checkStaffUsernameAvailability,
  createApprovedField,
  createOrganization,
  createScholarshipStandard,
  createStaffUser,
  deactivateOrganization,
  getApprovedFieldUsage,
  getReviewTemplateUsage,
  listApprovedFields,
  listFundingPolicyOptions,
  listMentoringPolicyOptions,
  listOrganizations,
  listReviewTemplates,
  listRenewalPolicyOptions,
  listScholarshipStandards,
  listScholarshipWorkflowTemplates,
  listStaffUsers,
  OrganizationProjection,
  reactivateStaffUser,
  provisionWorkflowConfig,
  resetStaffAccess,
  retireApprovedField,
  retireReviewTemplate as retireReviewTemplateApi,
  ReviewTemplateProjection,
  ReviewTemplateUsageProjection,
  ScholarshipStandardProjection,
  StaffRoleValue,
  StaffUserProjection,
  suspendStaffUser,
  updateReviewTemplate,
  updateStaffRole,
  verifyStaffUser,
  versionReviewTemplate,
  WorkflowTemplateLibraryProjection,
  FundingPolicyOptionProjection,
} from "@/api/adminApi";
import {
  fetchTenantGovernancePolicy,
  TenantGovernancePolicyProjection,
} from "@/api/tenantGovernanceApi";

import {
  ApprovedFieldForm,
  duplicateWarnings,
  OrganizationForm,
  ScholarshipStandardForm,
  StaffForm,
  SuperAdminScreen,
} from "./shared";

const INITIAL_FIELD_FORM: ApprovedFieldForm = {
  label: "",
  category: "Eligibility",
  field_type: "text",
  importance: "Medium",
  review_impact: "No additional review",
  description: "",
  why_needed: "",
};

const INITIAL_ORGANIZATION_FORM: OrganizationForm = {
  name: "",
  deactivate_reason: "",
};

const INITIAL_STAFF_FORM: StaffForm = {
  username: "",
  password: "",
  role: "pm",
  email: "",
  phone: "",
  change_reason: "",
  reset_password: "",
};

const INITIAL_SCHOLARSHIP_STANDARD_FORM: ScholarshipStandardForm = {
  template_code: "",
  description: "",
  preset: "standard",
  workflow_template_id: "",
  funding_type: "single_year",
  number_of_years: "1",
  renewal_rule: "automatic",
  mentoring_completion_criteria: [],
  version_code: "",
  retire_reason: "",
  retire_confirm: false,
  edit_template_id: null,
};

const USERNAME_PATTERN = /^[a-zA-Z0-9._-]+$/;
const SCREEN_STORAGE_KEY = "super_admin_screen";

function readStoredScreen(): SuperAdminScreen {
  if (typeof window === "undefined") return "fields";
  if (new URLSearchParams(window.location.search).get("section") === "workflows") {
    return "workflows";
  }
  const stored = sessionStorage.getItem(SCREEN_STORAGE_KEY);
  if (stored === "launch" || stored === "users") return "organizations";
  if (stored === "scholarship-standards" || stored === "template-usage") {
    return "workflows";
  }
  if (
    stored === "fields" ||
    stored === "create-field" ||
    stored === "field-usage" ||
    stored === "workflows" ||
    stored === "organizations" ||
    stored === "responsibilities"
  ) {
    return stored;
  }
  return "fields";
}

export type UsernameAvailabilityState =
  | "idle"
  | "checking"
  | "available"
  | "taken"
  | "invalid";

export function useSuperAdminControlCenter() {
  const [screen, setScreen] = useState<SuperAdminScreen>(readStoredScreen);
  const [organizations, setOrganizations] = useState<OrganizationProjection[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(() => {
    const stored = Number(sessionStorage.getItem("tenant_id"));
    return Number.isFinite(stored) && stored > 0 ? stored : null;
  });
  const [fields, setFields] = useState<ApprovedFieldProjection[]>([]);
  const [reviewTemplates, setReviewTemplates] = useState<ReviewTemplateProjection[]>(
    []
  );
  const [workflowTemplateLibrary, setWorkflowTemplateLibrary] = useState<
    WorkflowTemplateLibraryProjection[]
  >([]);
  const [scholarshipStandards, setScholarshipStandards] = useState<
    ScholarshipStandardProjection[]
  >([]);
  const [renewalPolicyOptions, setRenewalPolicyOptions] = useState<
    { renewal_rule: string; name: string }[]
  >([]);
  const [fundingPolicyOptions, setFundingPolicyOptions] = useState<
    FundingPolicyOptionProjection[]
  >([]);
  const [mentoringCriteriaOptions, setMentoringCriteriaOptions] = useState<
    { criterion: string; label: string }[]
  >([]);
  const [projection, setProjection] = useState<TenantGovernancePolicyProjection | null>(
    null
  );
  const [staff, setStaff] = useState<StaffUserProjection[]>([]);
  const [usernameAvailability, setUsernameAvailability] =
    useState<UsernameAvailabilityState>("idle");
  const [selectedField, setSelectedField] = useState<ApprovedFieldProjection | null>(
    null
  );
  const [selectedFieldUsage, setSelectedFieldUsage] =
    useState<ApprovedFieldUsageProjection | null>(null);
  const [selectedTemplate, setSelectedTemplate] =
    useState<ReviewTemplateProjection | null>(null);
  const [selectedTemplateUsage, setSelectedTemplateUsage] =
    useState<ReviewTemplateUsageProjection | null>(null);
  const [retiringTemplate, setRetiringTemplate] =
    useState<ReviewTemplateProjection | null>(null);
  const [fieldUsageLoading, setFieldUsageLoading] = useState(false);
  const [templateUsageLoading, setTemplateUsageLoading] = useState(false);
  const [fieldForm, setFieldForm] = useState<ApprovedFieldForm>(INITIAL_FIELD_FORM);
  const [scholarshipStandardForm, setScholarshipStandardForm] =
    useState<ScholarshipStandardForm>(INITIAL_SCHOLARSHIP_STANDARD_FORM);
  const [fieldRetireReason, setFieldRetireReason] = useState("");
  const [organizationForm, setOrganizationForm] = useState<OrganizationForm>(
    INITIAL_ORGANIZATION_FORM
  );
  const [staffForm, setStaffForm] = useState<StaffForm>(INITIAL_STAFF_FORM);
  const [duplicateOverride, setDuplicateOverride] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selectedOrganization = useMemo(
    () => organizations.find((organization) => organization.id === selectedTenantId) ?? null,
    [organizations, selectedTenantId]
  );

  useEffect(() => {
    const username = staffForm.username.trim();
    if (!username) {
      setUsernameAvailability("idle");
      return;
    }
    if (!USERNAME_PATTERN.test(username)) {
      setUsernameAvailability("invalid");
      return;
    }
    if (username.length < 3) {
      setUsernameAvailability("invalid");
      return;
    }

    let cancelled = false;
    setUsernameAvailability("checking");
    const timeout = window.setTimeout(() => {
      void checkStaffUsernameAvailability(username)
        .then((result) => {
          if (!cancelled) {
            setUsernameAvailability(result.available ? "available" : "taken");
          }
        })
        .catch(() => {
          if (!cancelled) {
            setUsernameAvailability("idle");
          }
        });
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [staffForm.username]);


  const activeProgramCount = useMemo(() => {
    const activeKeys = new Set(
      (projection?.workflow_configs ?? [])
        .filter((config) => config.is_active)
        .map((config) => `${config.program_code}:${config.academic_year}`)
    );
    return activeKeys.size;
  }, [projection?.workflow_configs]);

  const loadPage = useCallback(async (tenantIdOverride?: number | null) => {
    setLoading(true);
    setError(null);
    const messages: string[] = [];
    try {
      const requestedTenantId =
        typeof tenantIdOverride === "undefined" ? selectedTenantId : tenantIdOverride;
      const organizationRows = await listOrganizations();
      setOrganizations(organizationRows);

      const nextTenantId =
        organizationRows.find((organization) => organization.id === requestedTenantId)?.id ??
        organizationRows[0]?.id ??
        null;

      if (nextTenantId) {
        sessionStorage.setItem("tenant_id", String(nextTenantId));
        if (nextTenantId !== selectedTenantId) {
          setSelectedTenantId(nextTenantId);
        }

        const [
          fieldResult,
          templateResult,
          libraryResult,
          standardsResult,
          fundingResult,
          renewalResult,
          mentoringResult,
        ] = await Promise.allSettled([
          listApprovedFields(),
          listReviewTemplates(),
          listScholarshipWorkflowTemplates(),
          listScholarshipStandards(),
          listFundingPolicyOptions(),
          listRenewalPolicyOptions(),
          listMentoringPolicyOptions(),
        ]);

        if (fieldResult.status === "fulfilled") {
          setFields(fieldResult.value);
        } else {
          setFields([]);
          messages.push(readError(fieldResult.reason, "Approved fields could not be loaded."));
        }

        if (templateResult.status === "fulfilled") {
          setReviewTemplates(templateResult.value);
        } else {
          setReviewTemplates([]);
          messages.push(
            readError(templateResult.reason, "Reusable workflow setups could not be loaded.")
          );
        }
        if (libraryResult.status === "fulfilled") {
          setWorkflowTemplateLibrary(libraryResult.value);
          if (libraryResult.value[0]?.id) {
            setScholarshipStandardForm((current) => ({
              ...current,
              workflow_template_id:
                current.workflow_template_id || String(libraryResult.value[0].id),
            }));
          }
        } else {
          setWorkflowTemplateLibrary([]);
          messages.push(
            readError(libraryResult.reason, "Workflow template library could not be loaded.")
          );
        }
        if (standardsResult.status === "fulfilled") {
          setScholarshipStandards(standardsResult.value);
        } else {
          setScholarshipStandards([]);
          messages.push(
            readError(standardsResult.reason, "Reusable workflow setups could not be loaded.")
          );
        }
        if (fundingResult.status === "fulfilled") {
          setFundingPolicyOptions(fundingResult.value);
        } else {
          setFundingPolicyOptions([]);
        }
        if (renewalResult.status === "fulfilled") {
          setRenewalPolicyOptions(renewalResult.value);
        } else {
          setRenewalPolicyOptions([]);
        }
        if (mentoringResult.status === "fulfilled") {
          setMentoringCriteriaOptions(mentoringResult.value.completion_criteria);
        } else {
          setMentoringCriteriaOptions([]);
        }

        const [policy, staffRows] = await Promise.all([
          fetchTenantGovernancePolicy(nextTenantId),
          listStaffUsers(nextTenantId),
        ]);
        setProjection(policy);
        setStaff(staffRows);
      } else {
        sessionStorage.removeItem("tenant_id");
        setFields([]);
        setReviewTemplates([]);
        setWorkflowTemplateLibrary([]);
        setScholarshipStandards([]);
        setFundingPolicyOptions([]);
        setRenewalPolicyOptions([]);
        setMentoringCriteriaOptions([]);
        setProjection(null);
        setStaff([]);
      }

      setError(messages.length > 0 ? messages.join(" ") : null);
    } catch (caught) {
      setError(readError(caught, "Could not load the Super Admin console."));
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  useEffect(() => {
    sessionStorage.setItem(SCREEN_STORAGE_KEY, screen);
  }, [screen]);

  function selectScreen(nextScreen: SuperAdminScreen) {
    if (
      screen === "workflows" &&
      nextScreen !== "workflows" &&
      sessionStorage.getItem("super_admin_workflow_unsaved") === "true" &&
      !window.confirm("Discard the unsaved workflow changes?")
    ) {
      return;
    }
    if (nextScreen !== "workflows") {
      sessionStorage.setItem("super_admin_workflow_unsaved", "false");
    }
    setScreen(nextScreen);
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (nextScreen === "workflows") {
      url.searchParams.set("section", "workflows");
    } else {
      url.searchParams.delete("section");
      url.searchParams.delete("workflow");
      url.searchParams.delete("workflowVersion");
    }
    window.history.replaceState({}, "", url);
  }

  async function selectOrganization(organization: OrganizationProjection) {
    setSelectedTenantId(organization.id);
    sessionStorage.setItem("tenant_id", String(organization.id));
    setMessage(`Opened ${organization.name}.`);
    await loadPage(organization.id);
  }

  async function createField() {
    setError(null);
    setMessage(null);
    const warnings = duplicateWarnings(fieldForm.label, fields);
    if (warnings.length > 0 && !duplicateOverride) {
      setError("This looks like a duplicate. Confirm the override before creating it.");
      return;
    }
    if (!fieldForm.why_needed.trim()) {
      setError("Explain why this field is needed before creating it.");
      return;
    }
    setSaving(true);
    try {
      await createApprovedField(fieldForm);
      setFieldForm(INITIAL_FIELD_FORM);
      setDuplicateOverride(false);
      setScreen("fields");
      setMessage("Approved field created.");
      await loadPage();
    } catch (caught) {
      setError(readError(caught, "Could not create the approved field."));
    } finally {
      setSaving(false);
    }
  }

  async function openUsage(field: ApprovedFieldProjection) {
    setSelectedField(field);
    setSelectedFieldUsage(null);
    setScreen("field-usage");
    setFieldUsageLoading(true);
    setError(null);
    try {
      setSelectedFieldUsage(await getApprovedFieldUsage(field.field_key));
    } catch (caught) {
      setError(readError(caught, "Could not load field usage."));
    } finally {
      setFieldUsageLoading(false);
    }
  }

  async function retireField(field: ApprovedFieldProjection) {
    if (!fieldRetireReason.trim()) {
      setError("A retirement reason is required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await retireApprovedField(field.field_key, fieldRetireReason);
      setFieldRetireReason("");
      setMessage(`${field.label} retired.`);
      await loadPage();
    } catch (caught) {
      setError(readError(caught, "Could not retire the approved field."));
    } finally {
      setSaving(false);
    }
  }

  function editTemplateDraft(template: ReviewTemplateProjection) {
    if (!canEditTemplate(template)) {
      setError(
        "This Scholarship Standard is locked. Create a new version to make changes."
      );
      return;
    }
    setScholarshipStandardForm({
      ...INITIAL_SCHOLARSHIP_STANDARD_FORM,
      template_code: template.template_code,
      description: template.description ?? "",
      preset: inferTemplatePreset(template),
      edit_template_id: template.id,
    });
    setMessage("Editing draft Scholarship Standard.");
    setError(null);
  }

  async function saveScholarshipStandard(): Promise<boolean> {
    setError(null);
    setMessage(null);
    if (scholarshipStandardForm.template_code.trim().length < 3) {
      setError("Standard name must be at least 3 characters.");
      return false;
    }
    const selectedWorkflowTemplate =
      workflowTemplateLibrary.find(
        (template) => String(template.id) === scholarshipStandardForm.workflow_template_id
      ) ?? workflowTemplateLibrary[0];
    if (!selectedWorkflowTemplate) {
      setError("Choose a recommended workflow.");
      return false;
    }
    if (
      selectedWorkflowTemplate.requires_mentoring &&
      scholarshipStandardForm.mentoring_completion_criteria.length === 0
    ) {
      setError("Choose at least one mentoring completion criterion.");
      return false;
    }
    setSaving(true);
    try {
      await createScholarshipStandard({
        name: scholarshipStandardForm.template_code.trim(),
        description: scholarshipStandardForm.description.trim() || null,
        workflow_template_id: selectedWorkflowTemplate.id,
        funding_type: scholarshipStandardForm.funding_type,
        number_of_years:
          scholarshipStandardForm.funding_type === "multi_year"
            ? Number(scholarshipStandardForm.number_of_years || 2)
            : 1,
        renewal_rule: scholarshipStandardForm.renewal_rule,
        mentoring_required: selectedWorkflowTemplate.requires_mentoring,
        mentoring_completion_criteria: selectedWorkflowTemplate.requires_mentoring
          ? scholarshipStandardForm.mentoring_completion_criteria
          : [],
        eligibility_config: {},
        evidence_config: {},
        scoring_config: {},
        budget_config: {},
      });
      setScholarshipStandardForm({
        ...INITIAL_SCHOLARSHIP_STANDARD_FORM,
        workflow_template_id: String(selectedWorkflowTemplate.id),
      });
      setMessage("Reusable workflow setup saved.");
      await loadPage();
      return true;
    } catch (caught) {
      setError(readError(caught, "Could not save the reusable workflow setup."));
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function activateTemplateAction(template: ReviewTemplateProjection) {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await activateReviewTemplate(template.id);
      setMessage("Scholarship Standard activated after validation.");
      await loadPage();
    } catch (caught) {
      setError(readError(caught, "Scholarship Standard activation failed validation."));
    } finally {
      setSaving(false);
    }
  }

  async function createTemplateVersion(template: ReviewTemplateProjection) {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await versionReviewTemplate(template.id, {
        template_code: scholarshipStandardForm.version_code.trim() || null,
        description: template.description ?? null,
      });
      setScholarshipStandardForm({ ...scholarshipStandardForm, version_code: "" });
      setMessage("New draft version created. Existing programs are unchanged.");
      await loadPage();
    } catch (caught) {
      setError(readError(caught, "Could not create a new Scholarship Standard version."));
    } finally {
      setSaving(false);
    }
  }

  async function openTemplateUsage(template: ReviewTemplateProjection) {
    setSelectedTemplate(template);
    setSelectedTemplateUsage(null);
    setScreen("workflows");
    setTemplateUsageLoading(true);
    setError(null);
    try {
      setSelectedTemplateUsage(await getReviewTemplateUsage(template.id));
    } catch (caught) {
      setError(readError(caught, "Could not load Scholarship Standard usage."));
    } finally {
      setTemplateUsageLoading(false);
    }
  }

  async function startRetireTemplate(template: ReviewTemplateProjection) {
    if (template.lifecycle_state !== "active") {
      setError("Only active Scholarship Standards can be retired.");
      return;
    }
    setRetiringTemplate(template);
    setSelectedTemplate(template);
    setSelectedTemplateUsage(null);
    setScholarshipStandardForm({
      ...scholarshipStandardForm,
      retire_reason: "",
      retire_confirm: false,
    });
    setTemplateUsageLoading(true);
    setError(null);
    try {
      setSelectedTemplateUsage(await getReviewTemplateUsage(template.id));
    } catch (caught) {
      setError(readError(caught, "Could not load retirement impact."));
    } finally {
      setTemplateUsageLoading(false);
    }
  }

  async function retireTemplateAction() {
    if (!retiringTemplate) {
      setError("Choose an active Scholarship Standard to retire.");
      return;
    }
    if (!scholarshipStandardForm.retire_reason.trim()) {
      setError("A retirement reason is required.");
      return;
    }
    if (!scholarshipStandardForm.retire_confirm) {
      setError("Confirm that retirement affects future use only.");
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await retireReviewTemplateApi(retiringTemplate.id, scholarshipStandardForm.retire_reason);
      setRetiringTemplate(null);
      setSelectedTemplateUsage(null);
      setScholarshipStandardForm(INITIAL_SCHOLARSHIP_STANDARD_FORM);
      setMessage("Scholarship Standard retired. Existing programs and applications are unchanged.");
      await loadPage();
    } catch (caught) {
      setError(readError(caught, "Could not retire the Scholarship Standard."));
    } finally {
      setSaving(false);
    }
  }

  async function createOrganizationAction() {
    setError(null);
    setMessage(null);
    if (organizationForm.name.trim().length < 3) {
      setError("Organization name must be at least 3 characters.");
      return;
    }
    setSaving(true);
    try {
      const created = await createOrganization({ name: organizationForm.name.trim() });
      setSelectedTenantId(created.id);
      sessionStorage.setItem("tenant_id", String(created.id));
      setOrganizationForm(INITIAL_ORGANIZATION_FORM);
      setMessage(`${created.name} created and selected.`);
      await loadPage(created.id);
    } catch (caught) {
      setError(readError(caught, "Could not create the organization."));
    } finally {
      setSaving(false);
    }
  }

  async function deactivateOrganizationAction(organization: OrganizationProjection) {
    setError(null);
    if (!organizationForm.deactivate_reason.trim()) {
      setError("Enter a deactivation reason first.");
      return;
    }
    setSaving(true);
    try {
      await deactivateOrganization(organization.id, organizationForm.deactivate_reason);
      setOrganizationForm(INITIAL_ORGANIZATION_FORM);
      setMessage(`${organization.name} deactivated.`);
      await loadPage();
    } catch (caught) {
      setError(readError(caught, "Could not deactivate the organization."));
    } finally {
      setSaving(false);
    }
  }

  async function createStaffUserAction() {
    setError(null);
    if (!selectedTenantId) {
      setError("Select an organization first.");
      return;
    }
    const username = staffForm.username.trim();
    if (!USERNAME_PATTERN.test(username) || username.length < 3) {
      setError("Username may contain only letters, numbers, dot, underscore, and hyphen.");
      return;
    }
    if (usernameAvailability === "taken") {
      setError("Username is already taken.");
      return;
    }
    setSaving(true);
    try {
      await createStaffUser({
        tenant_id: selectedTenantId,
        username,
        password: staffForm.password,
        role: staffForm.role,
        email: staffForm.email.trim(),
        phone: staffForm.phone.trim(),
      });
      setStaffForm(INITIAL_STAFF_FORM);
      setUsernameAvailability("idle");
      setMessage("Staff user added.");
      await loadPage(selectedTenantId);
    } catch (caught) {
      setError(readError(caught, "Could not add the staff user."));
    } finally {
      setSaving(false);
    }
  }

  async function changeRole(userId: number, role: StaffRoleValue) {
    if (!staffForm.change_reason.trim()) {
      setError("Enter a reason for access changes before changing a role.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateStaffRole(userId, { role, reason: staffForm.change_reason });
      setMessage("Staff role updated.");
      await loadPage(selectedTenantId);
    } catch (caught) {
      setError(readError(caught, "Could not update the staff role."));
    } finally {
      setSaving(false);
    }
  }

  async function verifyStaff(userId: number) {
    setSaving(true);
    setError(null);
    try {
      await verifyStaffUser(userId);
      setMessage("User verified. Activate access before this person can log in.");
      await loadPage(selectedTenantId);
    } catch (caught) {
      setError(readError(caught, "Could not verify the user."));
    } finally {
      setSaving(false);
    }
  }

  async function approveStaffAccess(userId: number) {
    const reason =
      staffForm.change_reason.trim() || "Approved staff access from Super Admin console.";
    setSaving(true);
    setError(null);
    try {
      await verifyStaffUser(userId);
      await reactivateStaffUser(userId, reason);
      setStaffForm({ ...staffForm, change_reason: "" });
      setMessage("Staff access approved and activated. This user can log in.");
      await loadPage(selectedTenantId);
    } catch (caught) {
      setError(readError(caught, "Could not approve and activate staff access."));
    } finally {
      setSaving(false);
    }
  }

  async function resetAccess(userId: number) {
    if (!staffForm.change_reason.trim() || !staffForm.reset_password) {
      setError("Enter a reason and new password before resetting access.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await resetStaffAccess(userId, staffForm.reset_password, staffForm.change_reason);
      setMessage("Access reset.");
      setStaffForm({ ...staffForm, reset_password: "" });
      await loadPage(selectedTenantId);
    } catch (caught) {
      setError(readError(caught, "Could not reset access."));
    } finally {
      setSaving(false);
    }
  }

  async function suspendStaff(userId: number) {
    if (!staffForm.change_reason.trim()) {
      setError("Enter a reason before suspending access.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await suspendStaffUser(userId, staffForm.change_reason);
      setMessage("Staff access suspended.");
      await loadPage(selectedTenantId);
    } catch (caught) {
      setError(readError(caught, "Could not suspend access."));
    } finally {
      setSaving(false);
    }
  }

  async function reactivateStaff(userId: number) {
    if (!staffForm.change_reason.trim()) {
      setError("Enter a reason before reactivating access.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await reactivateStaffUser(userId, staffForm.change_reason);
      setStaffForm({ ...staffForm, change_reason: "" });
      setMessage("Staff access activated. This user can log in.");
      await loadPage(selectedTenantId);
    } catch (caught) {
      setError(readError(caught, "Could not reactivate access."));
    } finally {
      setSaving(false);
    }
  }

  async function provisionSelectedReviewProcess(programCode: string, academicYear: string) {
    if (!selectedTenantId) {
      setError("Select an organization before provisioning a review process.");
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await provisionWorkflowConfig({
        tenant_id: selectedTenantId,
        program_code: programCode,
        academic_year: academicYear,
      });
      setMessage("Selected review process provisioned.");
      await loadPage();
    } catch (caught) {
      setError(readError(caught, "Could not provision the selected review process."));
    } finally {
      setSaving(false);
    }
  }

  return {
    activeProgramCount,
    approveStaffAccess,
    createField,
    createOrganizationAction,
    createStaffUserAction,
    changeRole,
    deactivateOrganizationAction,
    duplicateOverride,
    error,
    fieldForm,
    fieldRetireReason,
    fields,
    fieldUsageLoading,
    fundingPolicyOptions,
    loading,
    loadPage,
    message,
    openUsage,
    organizationForm,
    organizations,
    projection,
    provisionSelectedReviewProcess,
    resetAccess,
    activateTemplateAction,
    retireField,
    retireTemplateAction,
    reviewTemplates,
    workflowTemplateLibrary,
    scholarshipStandards,
    renewalPolicyOptions,
    mentoringCriteriaOptions,
    saving,
    screen,
    selectOrganization,
    selectedField,
    selectedFieldUsage,
    selectedTemplate,
    selectedTemplateUsage,
    selectedOrganization,
    selectedTenantId,
    setDuplicateOverride,
    setFieldForm,
    setFieldRetireReason,
    setOrganizationForm,
    setScreen: selectScreen,
    setStaffForm,
    setScholarshipStandardForm,
    editTemplateDraft,
    saveScholarshipStandard,
    staff,
    staffForm,
    usernameAvailability,
    startRetireTemplate,
    retiringTemplate,
    suspendStaff,
    reactivateStaff,
    scholarshipStandardForm,
    templateUsageLoading,
    createTemplateVersion,
    openTemplateUsage,
    verifyStaff,
  };
}

function canEditTemplate(template: ReviewTemplateProjection): boolean {
  return (
    template.lifecycle_state === "draft" &&
    Number(template.usage.review_processes_derived) === 0
  );
}

function inferTemplatePreset(
  template: ReviewTemplateProjection
): ScholarshipStandardForm["preset"] {
  const statuses = new Set(template.transitions.map((transition) => transition.to_status));
  if (statuses.has("interview")) return "interview";
  return "standard";
}

function normalizeErrorDetail(detail: unknown): string | null {
  if (typeof detail === "string") {
    return detail;
  }
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }
        if (item && typeof item === "object" && "msg" in item) {
          const message = String(item.msg);
          if ("loc" in item && Array.isArray(item.loc)) {
            const field = item.loc[item.loc.length - 1];
            return field ? `${String(field)}: ${message}` : message;
          }
          return message;
        }
        return null;
      })
      .filter((message): message is string => Boolean(message));
    return messages.length > 0 ? messages.join(" ") : null;
  }
  if (detail && typeof detail === "object" && "message" in detail) {
    return String(detail.message);
  }
  return null;
}

function readError(caught: unknown, fallback: string): string {
  if (
    caught &&
    typeof caught === "object" &&
    "response" in caught &&
    caught.response &&
    typeof caught.response === "object" &&
    "data" in caught.response
  ) {
    const data = caught.response.data as { detail?: unknown };
    return normalizeErrorDetail(data.detail) ?? fallback;
  }
  if (caught instanceof Error) {
    return caught.message;
  }
  return fallback;
}
